import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Transition as _Transition, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-0013ac0e"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "px-[16px] py-2" }
const _hoisted_2 = { class: "text-left" }

import { storeToRefs } from 'pinia'
import ModalVue from '@/components/designSystem/modals/Modal.vue'
import { EModals, useModalStore, type shareModalDataType } from '@/pinia-store/useModalStore'
import { computed, onUpdated, ref, watch } from 'vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import {
  AuthorizationRelationResponseExpanded,
  permissionPowerMap,
  resourceHierarchyMap
} from '@/Network/Types/Responses/AuthorizationResponses'
import {
  AuthorizationResourcesAndSubjects,
  AuthorizationRelations,
  AuthorizationPermissions,
  PermissionImpactResponse
} from '@/types/authorization'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useToast } from 'vue-toastification'
import { DEFAULT_MESSAGES } from '@/hooks/useCommon'
import { mdiAccount, mdiAccountMultiple } from '@mdi/js'
import { getApiErrorMessage, getApiErrorStatus } from '@/utils/apiError'
import Network from '@/Network'
import { UpsertPermissionsRequest } from '@/Network/Types/Requests/AuthorizationRequests'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import ModalHeader from './commons/ModalHeader.vue'
import SubjectsAutocomplete from '@/components/designSystem/Inputs/SubjectsAutocomplete.vue'
import PermissionsAccordionList from './commons/PermissionsAccordionList.vue'
import { ScheduleManagementStrategy } from './strategies/ScheduleManagementStrategy'
import { DsAttendanceStrategy } from './strategies/DsAttendanceStrategy'
import { StationExceptionalitiesStrategy } from './strategies/StationExceptionalitiesStrategy'
import { NpsStrategy } from './strategies/NpsStrategy'
import { PostStationStrategy } from './strategies/PostStationStrategy'
import type { ModuleTypeStrategy } from './strategies/ModuleTypeStrategy'

const REMOVE_ACCESS_VALUE = 'Remover Acesso'


export default /*#__PURE__*/_defineComponent({
  __name: 'share.modal',
  setup(__props) {

const { flags } = useFeatureFlags()

const modalName = EModals.SHARE
const { modals } = storeToRefs(useModalStore())
const modalStore = useModalStore()
const handleToggleModal = () => {
  clearFields()
  modalStore.toggleModal(modalName)
}
const toast = useToast()
const permissionsStore = usePermissionsStore()
const { resourceCandidates, isLoadingResourceCandidates, isLoadingShared } =
  storeToRefs(permissionsStore)

const isUnauthorize = ref(false)

const permissionsData = ref<AuthorizationRelationResponseExpanded[]>([])
const originPermissionsDataMap = new Map<number, AuthorizationRelations>()

const pendingChangesMap = ref(new Map<number, AuthorizationRelationResponseExpanded>())

const impactWarnings = ref<PermissionImpactResponse[]>([])
const isCheckingImpact = ref(false)

const MODULE_TYPE_STRATEGIES: Record<string, ModuleTypeStrategy> = {
  schedule_management: new ScheduleManagementStrategy(),
  ds_attendance: new DsAttendanceStrategy(),
  station_exceptionalities: new StationExceptionalitiesStrategy(),
  nps: new NpsStrategy(),
  post_station: new PostStationStrategy()
}

const ALL_MODULE_TYPE_OPTIONS = [
  { label: 'Gestão de Escalas', value: 'schedule_management' },
  { label: 'Registro de Plantão', value: 'ds_attendance' },
  { label: 'Métricas de Satisfação', value: 'nps' },
  { label: 'Validação de Excepcionalidade', value: 'station_exceptionalities' },
  { label: 'Pós-Plantão', value: 'post_station' }
]

const SCHEDULE_UNSUPPORTED_MODULES = new Set(['nps', 'post_station'])

const moduleTypeOptions = computed(() => {
  const base = metadata.value?.resourceType as AuthorizationResourcesAndSubjects
  if (base === AuthorizationResourcesAndSubjects.Schedule) {
    return ALL_MODULE_TYPE_OPTIONS.filter((opt) => !SCHEDULE_UNSUPPORTED_MODULES.has(opt.value))
  }
  return ALL_MODULE_TYPE_OPTIONS
})
const moduleType = ref('schedule_management')

const activeStrategy = computed((): ModuleTypeStrategy | null => {
  return MODULE_TYPE_STRATEGIES[moduleType.value] ?? null
})

const effectiveResourceType = computed((): AuthorizationResourcesAndSubjects => {
  const base = metadata.value?.resourceType as AuthorizationResourcesAndSubjects
  const strategy = activeStrategy.value
  if (!strategy) return base
  return strategy.resolveResourceType(base)
})

const searchText = ref('')
const isSearching = ref(false)
const isLoading = ref(false)
const isLoadingFilter = ref(false)

const showFilterInput = ref(false)
const filterText = ref('')

const showInheritance = ref<Record<number, boolean>>({})

const canEditAccessLevel = computed(() => {
  return permissionsStore.checkAnyRessources({
    permission: AuthorizationPermissions.AccessLevelEditing,
    resourceType: AuthorizationResourcesAndSubjects.Company
  })
})

const permissionGroups = computed(() => {
  return permissionsData.value.filter(
    (permission) => permission.subject_type === AuthorizationResourcesAndSubjects.Group
  )
})

const permissionUsers = computed(() => {
  return permissionsData.value.filter(
    (permission) => permission.subject_type === AuthorizationResourcesAndSubjects.User
  )
})

const subjects = computed(() => {
  return resourceCandidates.value.map((candidate) => ({
    title: candidate.name,
    subtitle: candidate.email || candidate.description || '',
    value: {
      id: String(candidate.id),
      type: candidate.type
    },
    icon:
      candidate.type === AuthorizationResourcesAndSubjects.User ? mdiAccount : mdiAccountMultiple,
    raw: candidate
  }))
})

// === MUDANÇA PRINCIPAL AQUI ===
// A lista de baixo (permissões existentes) some se:
// 1. Tiver itens selecionados para compartilhar (subjectsToShare > 0)
// 2. OU se o usuário estiver digitando na busca principal (searchText > 0)
const isShareContext = computed(() => {
  return subjectsToShare.value.length > 0 || (searchText.value && searchText.value.length > 0)
})

function debounce<T extends (...args: any[]) => any>(fn: T, delay: number) {
  let timeoutId: ReturnType<typeof setTimeout>
  return (...args: Parameters<T>) => {
    if (timeoutId) clearTimeout(timeoutId)
    timeoutId = setTimeout(() => fn(...args), delay)
  }
}

const onEndReached = (isIntersecting: boolean) => {
  if (
    isIntersecting &&
    permissionsStore.candidatePagination.hasMore &&
    !isLoadingResourceCandidates.value
  ) {
    if (!metadata.value) return
    permissionsStore.fetchResourceCandidates(
      effectiveResourceType.value,
      Number(metadata.value.resourceId),
      Number(metadata.value.companyId),
      searchText.value,
      true
    )
  }
}

const onEndReachedSharedPermissions = (isIntersecting: boolean) => {
  if (
    isIntersecting &&
    permissionsStore.sharedPermissionsPagination.hasMore &&
    !isLoadingShared.value
  ) {
    fetchPermissions(filterText.value, true)
  }
}

watch(
  searchText,
  debounce(async (term: string) => {
    if (!modals.value[modalName] || !metadata.value) return

    await permissionsStore.fetchResourceCandidates(
      effectiveResourceType.value,
      Number(metadata.value.resourceId),
      Number(metadata.value.companyId),
      term,
      false
    )
  }, 300)
)

watch(
  filterText,
  debounce(async (val: string) => {
    if (!modals.value[modalName] || isShareContext.value) return

    isLoadingFilter.value = true
    try {
      await fetchPermissions(val, false)
    } finally {
      isLoadingFilter.value = false
    }
  }, 400)
)

const fetchPermissions = async (search?: string, isLoadMore = false) => {
  try {
    if (!metadata.value) return

    const companyId = Number(metadata.value.companyId)

    const permissions = await permissionsStore.fetchSharedPermissions(
      effectiveResourceType.value,
      metadata.value.resourceId,
      companyId,
      search,
      isLoadMore
    )

    const mappedPermissions = permissions.map((p) => {
      if (pendingChangesMap.value.has(p.id)) {
        return { ...p, resource_relation: pendingChangesMap.value.get(p.id)!.resource_relation }
      }
      return p
    }) as AuthorizationRelationResponseExpanded[]

    if (isLoadMore) {
      permissionsData.value.push(...mappedPermissions)
    } else {
      permissionsData.value = mappedPermissions
      if (!search) {
        originPermissionsDataMap.clear()
      }
    }

    mappedPermissions.forEach((permission) => {
      if (!originPermissionsDataMap.has(permission.subject_id)) {
        originPermissionsDataMap.set(permission.subject_id, permission.resource_relation)
      }
    })
  } catch (error) {
    // `getApiErrorStatus` desembrulha o ServiceError do PermissionService; o
    // `axios.isAxiosError` antigo deixava de casar e o 403 caía no vazio.
    if (getApiErrorStatus(error) === 403) {
      isUnauthorize.value = true
    } else {
      // Antes: qualquer outra falha era engolida e a lista simplesmente não aparecia.
      toast.error(
        getApiErrorMessage(error, 'Não foi possível carregar as permissões compartilhadas.')
      )
    }
  }
}

const getPendingRelation = (subjectId: number): string | null => {
  return (pendingChangesMap.value.get(subjectId)?.resource_relation as string | undefined) ?? null
}

const getEffectiveRelation = (subject: AuthorizationRelationResponseExpanded): string => {
  return (getPendingRelation(subject.subject_id) ?? subject.resource_relation) as string
}

const trackChange = (subject: AuthorizationRelationResponseExpanded, newValue: any) => {
  const object = pendingChangesMap.value.set(subject.subject_id, {
    ...subject,
    resource_relation: newValue
  })

  debouncedCheckImpact()

  return object
}

const fetchInitialCandidates = async () => {
  if (!metadata.value) return

  await permissionsStore.fetchResourceCandidates(
    effectiveResourceType.value,
    Number(metadata.value.resourceId),
    Number(metadata.value.companyId)
  )
}

onUpdated(async () => {
  if (!metadata.value || isLoading.value) return
})

watch(
  () => modals.value[modalName],
  async (isOpen) => {
    if (isOpen && metadata.value) {
      try {
        isLoading.value = true
        await Promise.all([fetchPermissions(), fetchInitialCandidates()])
      } finally {
        isLoading.value = false
      }
    } else {
      clearFields()
    }
  }
)

const permission = ref<AuthorizationRelations>(AuthorizationRelations.BasicEditor)

const COMMON_PERMISSIONS = {
  parent: { label: 'Proprietário', value: AuthorizationRelations.Parent, disabled: true },
  basicReader: { label: 'Leitor Básico', value: AuthorizationRelations.BasicReader },
  intermediateReader: {
    label: 'Leitor Intermediário',
    value: AuthorizationRelations.IntermediateReader
  },
  advancedReader: { label: 'Leitor Avançado', value: AuthorizationRelations.AdvancedReader },
  basicEditor: { label: 'Editor Básico', value: AuthorizationRelations.BasicEditor },
  intermediateEditor: {
    label: 'Editor Intermediário',
    value: AuthorizationRelations.IntermediateEditor
  },
  contentAdmin: { label: 'Administrador de Conteúdo', value: AuthorizationRelations.ContentAdmin },
  generalAdmin: { label: 'Administrador Geral', value: AuthorizationRelations.GeneralAdmin },
  emitter: { label: 'Emissor', value: AuthorizationRelations.Emitter },
  basicReceiver: { label: 'Receptor Básico', value: AuthorizationRelations.BasicReceiver },
  advancedReceiver: { label: 'Receptor Avançado', value: AuthorizationRelations.AdvancedReceiver },
  extractor: { label: 'Extrator', value: AuthorizationRelations.Extractor },
  qualityAdmin: { label: 'Configurador', value: AuthorizationRelations.QualityAdmin },
  technicalEvaluator: {
    label: 'Avaliador Técnico',
    value: AuthorizationRelations.TechnicalEvaluator
  },
  institutionalEvaluator: {
    label: 'Avaliador Institucional',
    value: AuthorizationRelations.InstitutionalEvaluator
  },
  analyticsViewer: {
    label: 'Analista de Resultados',
    value: AuthorizationRelations.AnalyticsViewer
  },
  operator: { label: 'Operação', value: AuthorizationRelations.Operator },
  medicalCoordinator: {
    label: 'Tático',
    value: AuthorizationRelations.MedicalCoordinator
  },
  auditAnalyst: {
    label: 'Análise de Risco',
    value: AuthorizationRelations.AuditAnalyst
  },
  technicalValidator: {
    label: 'Técnica',
    value: AuthorizationRelations.TechnicalValidator
  },
  strategicValidator: {
    label: 'Estratégica',
    value: AuthorizationRelations.StrategicValidator
  },
  executiveValidator: {
    label: 'Executiva',
    value: AuthorizationRelations.ExecutiveValidator
  },
  documentationAnalyst: {
    label: 'Analista de Documentação',
    value: AuthorizationRelations.DocumentationAnalyst
  },
  financialAnalyst: {
    label: 'Analista Financeiro',
    value: AuthorizationRelations.FinancialAnalyst
  }
}

const permissionNewUsers = computed(() => {
  const strategy = activeStrategy.value
  if (strategy) return strategy.getPermissionsForNewUsers()
  return [
    COMMON_PERMISSIONS.basicReader,
    COMMON_PERMISSIONS.intermediateReader,
    COMMON_PERMISSIONS.advancedReader,
    COMMON_PERMISSIONS.basicEditor,
    COMMON_PERMISSIONS.intermediateEditor,
    COMMON_PERMISSIONS.contentAdmin,
    COMMON_PERMISSIONS.generalAdmin
  ]
})

const subjectsToShare = ref<{ id: string; type: AuthorizationResourcesAndSubjects }[]>([])
const isNotifyShare = ref<boolean>(true)
const messageNotify = ref<string>('')

const handleAddPermissions = async (
  resourceId: number,
  resourceType: AuthorizationResourcesAndSubjects,
  companyId: number
) => {
  const payload: UpsertPermissionsRequest = {
    resource_id: +resourceId,
    message: messageNotify.value,
    notify_share: isNotifyShare.value,
    subjects: subjectsToShare.value.map((subject) => {
      return {
        resource_relation: permission.value,
        subject_id: Number(subject.id),
        subject_type: subject.type
      }
    })
  }

  const strategy = activeStrategy.value
  if (strategy) {
    await strategy.addPermissions(resourceId, companyId, payload, effectiveResourceType.value)
    return
  }

  await Network.permission.addPermissions(resourceId, resourceType, payload, companyId)
}

const handleUpdatePermissions = async (
  resourceId: number,
  resourceType: AuthorizationResourcesAndSubjects,
  companyId: number
) => {
  const subjectsToUpdate = Array.from(pendingChangesMap.value.values()).flatMap((permission) => {
    if (originPermissionsDataMap.get(permission.subject_id) === permission.resource_relation)
      return []

    const relationValue = permission.resource_relation as string
    const isDelete = relationValue === REMOVE_ACCESS_VALUE

    const originalRelation = originPermissionsDataMap.get(permission.subject_id)

    if (!isDelete && originalRelation === permission.resource_relation) return []

    let relationToSend = permission.resource_relation
    if (isDelete) {
      relationToSend = originalRelation || AuthorizationRelations.BasicReader
    }

    return [
      {
        id: permission.subject_id,
        resource_relation: relationToSend,
        subject_id: Number(permission.subject_id),
        subject_type: permission.subject_type,
        isDelete
      }
    ]
  })

  if (subjectsToUpdate.length === 0) return

  const payload: UpsertPermissionsRequest = {
    resource_id: resourceId,
    subjects: subjectsToUpdate
  }

  const strategy = activeStrategy.value
  if (strategy) {
    await strategy.upsertPermissions(resourceId, companyId, payload, effectiveResourceType.value)
    return
  }

  await Network.permission.upsertPermissions(resourceId, resourceType, payload, companyId)
}

const handleSubmit = async () => {
  if (!metadata.value) return
  try {
    isLoading.value = true

    const rType = effectiveResourceType.value
    const companyId = Number(metadata.value.companyId)

    if (isShareContext.value) {
      await handleAddPermissions(metadata.value.resourceId, rType, companyId)
      toast.success('Permissões adicionadas com sucesso')
    } else {
      await handleUpdatePermissions(metadata.value.resourceId, rType, companyId)
      toast.success('Permissões atualizadas com sucesso')
    }

    handleToggleModal()
  } catch (err) {
    // Antes: `err.response.data.message` extraído na mão — com o ServiceError do
    // PermissionService isso viraria `undefined` e a mensagem do backend sumiria.
    toast.error(getApiErrorMessage(err, DEFAULT_MESSAGES.error))
  } finally {
    isLoading.value = false
  }
}

const permissionsForSelect = computed(() => {
  const strategy = activeStrategy.value
  if (strategy) return strategy.getPermissionsForSelect()
  return [
    COMMON_PERMISSIONS.parent,
    COMMON_PERMISSIONS.basicReader,
    COMMON_PERMISSIONS.intermediateReader,
    COMMON_PERMISSIONS.advancedReader,
    COMMON_PERMISSIONS.basicEditor,
    COMMON_PERMISSIONS.intermediateEditor,
    COMMON_PERMISSIONS.contentAdmin,
    COMMON_PERMISSIONS.generalAdmin
  ]
})

const metadata = computed((): shareModalDataType | undefined => {
  return modalStore.metadata as shareModalDataType | undefined
})

const shareTitle = computed(() => {
  if (metadata.value?.breadcrumb && metadata.value.breadcrumb.length > 0) {
    return `Compartilhar "${metadata.value.breadcrumb[metadata.value.breadcrumb.length - 1]}"`
  }
  return `Compartilhar "${metadata.value?.resourceName || ''}"`
})

const visibleBreadcrumbs = computed(() => {
  if (metadata.value?.breadcrumb && metadata.value.breadcrumb.length > 1) {
    return metadata.value.breadcrumb
  }
  return []
})

const clearFilter = () => {
  showFilterInput.value = false
  filterText.value = ''
  fetchPermissions()
}

function clearFields() {
  subjectsToShare.value = []
  permissionsData.value = []
  messageNotify.value = ''
  isUnauthorize.value = false
  searchText.value = ''
  impactWarnings.value = []
  isCheckingImpact.value = false
  pendingChangesMap.value.clear()
  originPermissionsDataMap.clear()
  permissionsStore.clearResourceCandidates()
  permissionsStore.resetSharedPagination()
  clearFilter()
  showInheritance.value = {}
  moduleType.value = 'schedule_management'
}

const isMarkedForRemoval = (id: number) => {
  return (pendingChangesMap.value.get(id)?.resource_relation as any) === REMOVE_ACCESS_VALUE
}

const toggleRemoval = (subject: AuthorizationRelationResponseExpanded) => {
  if (isMarkedForRemoval(subject.subject_id)) {
    const pendingObj = pendingChangesMap.value.get(subject.subject_id) as any
    const restoreValue =
      pendingObj?._previous_relation || originPermissionsDataMap.get(subject.subject_id)

    if (restoreValue) {
      trackChange(subject, restoreValue)
    }

    if (restoreValue === originPermissionsDataMap.get(subject.subject_id)) {
      pendingChangesMap.value.delete(subject.subject_id)
    }
  } else {
    const currentRelation = getEffectiveRelation(subject)

    pendingChangesMap.value.set(subject.subject_id, {
      ...subject,
      resource_relation: REMOVE_ACCESS_VALUE as any,
      _previous_relation: currentRelation
    } as any)
  }

  debouncedCheckImpact()
}

const ALL_RELATION_LABELS = Object.fromEntries(
  Object.values(COMMON_PERMISSIONS).map((p) => [p.value, p.label])
)

const getRelationLabel = (relation: string) => {
  const found = permissionsForSelect.value.find((p) => p.value === relation)
  return found?.label ?? ALL_RELATION_LABELS[relation] ?? relation
}

const getSubjectName = (id: number, type: string) => {
  const existing = permissionsData.value.find((p) => p.subject_id === id && p.subject_type === type)
  if (existing) return existing.subject_name

  const candidate = subjects.value.find((s) => Number(s.value.id) === id && s.value.type === type)
  if (candidate) return candidate.title

  return 'Usuário/Grupo'
}

const handleCheckImpact = async () => {
  if (!metadata.value) return

  const newSubjects = subjectsToShare.value.map((s) => ({
    subject_id: Number(s.id),
    subject_type: s.type
  }))

  const pendingSubjects = Array.from(pendingChangesMap.value.values()).map((p) => ({
    subject_id: Number(p.subject_id),
    subject_type: p.subject_type
  }))

  const allSubjectsMap = new Map()
  ;[...newSubjects, ...pendingSubjects].forEach((s) => {
    const key = `${s.subject_type}:${s.subject_id}`
    allSubjectsMap.set(key, s)
  })

  const finalSubjects = Array.from(allSubjectsMap.values())

  if (finalSubjects.length === 0) {
    impactWarnings.value = []
    return
  }

  isCheckingImpact.value = true
  try {
    impactWarnings.value = await permissionsStore.checkPermissionImpact({
      resource_id: metadata.value.resourceId,
      resource_type: effectiveResourceType.value,
      subjects: finalSubjects
    })
  } finally {
    isCheckingImpact.value = false
  }
}

const debouncedCheckImpact = debounce(handleCheckImpact, 500)

watch(moduleType, () => {
  const strategy = activeStrategy.value
  permission.value = strategy ? strategy.getDefaultPermission() : AuthorizationRelations.BasicEditor

  if (modals.value[modalName] && metadata.value) {
    isLoading.value = true

    subjectsToShare.value = []
    searchText.value = ''
    permissionsStore.clearResourceCandidates()

    Promise.all([fetchPermissions(), fetchInitialCandidates()]).finally(() => {
      isLoading.value = false
    })
  }
})

watch(
  subjectsToShare,
  () => {
    debouncedCheckImpact()
  },
  { deep: true }
)

const toggleInheritance = (id: number, forceState?: boolean) => {
  if (forceState !== undefined) {
    showInheritance.value = { ...showInheritance.value, [id]: forceState }
  } else {
    showInheritance.value = { ...showInheritance.value, [id]: !showInheritance.value[id] }
  }
}

const translateResourceType = (type?: string | AuthorizationResourcesAndSubjects) => {
  if (!type) return ''
  const map: Record<string, string> = {
    [AuthorizationResourcesAndSubjects.Company]: 'Empresa',
    [AuthorizationResourcesAndSubjects.HealthUnit]: 'Unidade',
    [AuthorizationResourcesAndSubjects.Schedule]: 'Escala',
    [AuthorizationResourcesAndSubjects.DsAttendanceCompany]: 'Empresa',
    [AuthorizationResourcesAndSubjects.DsAttendanceHealthUnit]: 'Unidade',
    [AuthorizationResourcesAndSubjects.DsAttendance]: 'Escala',
    [AuthorizationResourcesAndSubjects.NpsCompany]: 'Empresa',
    [AuthorizationResourcesAndSubjects.NpsHealthUnit]: 'Unidade',
    [AuthorizationResourcesAndSubjects.PostStationCompany]: 'Empresa',
    [AuthorizationResourcesAndSubjects.PostStationHealthUnit]: 'Unidade',
    [AuthorizationResourcesAndSubjects.StationExceptionalitiesCompany]: 'Empresa',
    [AuthorizationResourcesAndSubjects.StationExceptionalitiesHealthUnit]: 'Unidade',
    [AuthorizationResourcesAndSubjects.StationExceptionalitiesSchedule]: 'Escala'
  }
  return map[type as string] || type
}

const getHighestInheritedPower = (subject: AuthorizationRelationResponseExpanded): number => {
  if (!subject.inheritances || subject.inheritances.length === 0) return 0
  let maxPower = 0
  subject.inheritances.forEach((inh) => {
    const power = permissionPowerMap[inh.resource_relation as AuthorizationRelations] || 0
    if (power > maxPower) maxPower = power
  })
  return maxPower
}

const getSelectablePermissions = (subject: AuthorizationRelationResponseExpanded) => {
  const minPowerRequired = getHighestInheritedPower(subject)
  return permissionsForSelect.value.map((p) => ({
    ...p,
    disabled:
      !!('disabled' in p && p.disabled) ||
      (permissionPowerMap[p.value as AuthorizationRelations] ?? 0) <= minPowerRequired
  }))
}

const resolveResourceName = (permItem: any) => {
  if (permItem.resource_name) return permItem.resource_name

  if (metadata.value?.breadcrumb && metadata.value.breadcrumb.length > 0) {
    const itemLevel = resourceHierarchyMap[permItem.resource_type]
    if (itemLevel && itemLevel > 0 && metadata.value.breadcrumb.length >= itemLevel) {
      return metadata.value.breadcrumb[itemLevel - 1]
    }
  }

  return `ID: ${permItem.resource_id}`
}

const getSortedPermissionsForDisplay = (subject: AuthorizationRelationResponseExpanded) => {
  const currentTypeId = metadata.value?.resourceId
  const currentType = effectiveResourceType.value

  const items = (subject.inheritances || []).map((inh) => {
    const isMatch =
      inh.resource_type === currentType && Number(inh.resource_id) === Number(currentTypeId)

    return {
      ...inh,
      resource_name: isMatch ? metadata.value?.resourceName : inh.resource_name,
      is_current_context: isMatch,
      is_inherited: true
    }
  })

  if (subject.resource_relation) {
    items.push({
      resource_type: subject.resource_type,
      resource_id: subject.resource_id,
      resource_relation: subject.resource_relation,
      resource_name: metadata.value?.resourceName,
      is_current_context: true,
      is_inherited: false
    })
  }

  return items.sort((a, b) => {
    const levelA = resourceHierarchyMap[a.resource_type] || 99
    const levelB = resourceHierarchyMap[b.resource_type] || 99
    return levelA - levelB
  })
}

return (_ctx: any,_cache: any) => {
  const _component_v_autocomplete = _resolveComponent("v-autocomplete")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_textarea = _resolveComponent("v-textarea")!
  const _component_v_checkbox = _resolveComponent("v-checkbox")!

  return (_openBlock(), _createBlock(ModalVue, {
    onClose: handleToggleModal,
    modalActive: _unref(modals)[_unref(modalName)],
    hasFooterBorder: false
  }, {
    header: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(ModalHeader, {
          resourceType: translateResourceType(metadata.value?.resourceType) || 'Recurso',
          resourceId: metadata.value?.resourceId,
          headerTitle: shareTitle.value,
          breadcrumbs: visibleBreadcrumbs.value
        }, null, 8, ["resourceType", "resourceId", "headerTitle", "breadcrumbs"])
      ])
    ]),
    footer: _withCtx(() => [
      _createVNode(_Transition, {
        name: "fade",
        mode: "out-in"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_row, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_col, {
                cols: "12",
                md: "6"
              }, {
                default: _withCtx(() => [
                  _createVNode(OutlineButton, {
                    onClick: handleToggleModal,
                    width: "100%",
                    text: "Cancelar"
                  })
                ]),
                _: 1
              }),
              _createVNode(_component_v_col, {
                cols: "12",
                md: "6"
              }, {
                default: _withCtx(() => [
                  _createVNode(FilledButton, {
                    onClick: handleSubmit,
                    text: isShareContext.value ? 'Enviar' : 'Concluido',
                    height: "52px",
                    width: "100%",
                    loading: isLoading.value,
                    disabled: isLoading.value || isUnauthorize.value || !canEditAccessLevel.value
                  }, null, 8, ["text", "loading", "disabled"])
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    default: _withCtx(() => [
      _createVNode(_component_v_row, {
        dense: "",
        class: "px-1 pt-2 pb-1"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_col, { cols: "6" }, {
            default: _withCtx(() => [
              _createVNode(_component_v_autocomplete, {
                modelValue: moduleType.value,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((moduleType).value = $event)),
                items: moduleTypeOptions.value,
                "item-title": "label",
                "item-value": "value",
                disabled: !_unref(flags).enabledDsAttendancePolicy,
                color: "primary20",
                variant: "outlined",
                density: "compact",
                "hide-details": ""
              }, null, 8, ["modelValue", "items", "disabled"])
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      _createVNode(_component_v_row, { dense: "" }, {
        default: _withCtx(() => [
          _createVNode(_component_v_col, { cols: "12" }, {
            default: _withCtx(() => [
              _createVNode(SubjectsAutocomplete, {
                modelValue: subjectsToShare.value,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((subjectsToShare).value = $event)),
                permission: permission.value,
                "onUpdate:permission": _cache[2] || (_cache[2] = ($event: any) => ((permission).value = $event)),
                items: subjects.value,
                "permission-options": permissionNewUsers.value,
                disabled: isLoading.value || !canEditAccessLevel.value,
                searching: isSearching.value,
                "has-more": _unref(permissionsStore).candidatePagination.hasMore,
                "loading-more": _unref(isLoadingResourceCandidates),
                "impact-warnings": impactWarnings.value,
                "get-subject-name": getSubjectName,
                "get-relation-label": getRelationLabel,
                "onUpdate:search": _cache[3] || (_cache[3] = ($event: any) => (searchText.value = $event)),
                onEndReached: _cache[4] || (_cache[4] = ($event: any) => (onEndReached(true)))
              }, null, 8, ["modelValue", "permission", "items", "permission-options", "disabled", "searching", "has-more", "loading-more", "impact-warnings"])
            ]),
            _: 1
          }),
          _createVNode(_Transition, {
            name: "slide-down",
            mode: "out-in"
          }, {
            default: _withCtx(() => [
              (isShareContext.value)
                ? (_openBlock(), _createBlock(_component_v_col, {
                    key: 0,
                    class: "mt-3"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_textarea, {
                        modelValue: messageNotify.value,
                        "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((messageNotify).value = $event)),
                        class: "message-to-share",
                        color: "primary20",
                        label: "Mensagem (Opcional)",
                        placeholder: "Escreva uma mensagem para notificar os usuários...",
                        disabled: !isNotifyShare.value
                      }, null, 8, ["modelValue", "disabled"]),
                      _createVNode(_component_v_checkbox, {
                        modelValue: isNotifyShare.value,
                        "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((isNotifyShare).value = $event)),
                        color: "primary",
                        fieldId: "isNotify",
                        label: "Notificar Pessoas"
                      }, null, 8, ["modelValue"])
                    ]),
                    _: 1
                  }))
                : (_openBlock(), _createBlock(_component_v_col, {
                    key: 1,
                    cols: "12",
                    class: "mt-2"
                  }, {
                    default: _withCtx(() => [
                      _createElementVNode("div", _hoisted_2, [
                        _createVNode(PermissionsAccordionList, {
                          "permission-groups": permissionGroups.value,
                          "permission-users": permissionUsers.value,
                          "filter-text": filterText.value,
                          "onUpdate:filterText": _cache[7] || (_cache[7] = ($event: any) => ((filterText).value = $event)),
                          "show-filter-input": showFilterInput.value,
                          "onUpdate:showFilterInput": _cache[8] || (_cache[8] = ($event: any) => ((showFilterInput).value = $event)),
                          "is-loading": isLoading.value,
                          "is-loading-filter": isLoadingFilter.value,
                          "has-more": _unref(permissionsStore).sharedPermissionsPagination.hasMore,
                          "loading-more": _unref(permissionsStore).isLoadingShared,
                          "show-inheritance": showInheritance.value,
                          "can-edit-access-level": canEditAccessLevel.value,
                          "is-marked-for-removal": isMarkedForRemoval,
                          "get-relation-label": getRelationLabel,
                          "get-selectable-permissions": getSelectablePermissions,
                          "get-sorted-permissions-for-display": getSortedPermissionsForDisplay,
                          "resolve-resource-name": resolveResourceName,
                          "get-pending-relation": getPendingRelation,
                          onClearFilter: clearFilter,
                          onToggleInheritance: toggleInheritance,
                          onTrackChange: _cache[9] || (_cache[9] = ({ subject, val }) => trackChange(subject, val)),
                          onToggleRemoval: toggleRemoval,
                          onEndReached: _cache[10] || (_cache[10] = ($event: any) => (onEndReachedSharedPermissions(true)))
                        }, null, 8, ["permission-groups", "permission-users", "filter-text", "show-filter-input", "is-loading", "is-loading-filter", "has-more", "loading-more", "show-inheritance", "can-edit-access-level"])
                      ])
                    ]),
                    _: 1
                  }))
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["modalActive"]))
}
}

})